import React from "react";
import Icon from "../../system/components/icon";
import TwemojiText from "../../system/components/twemoji-text";
import "../../system/components/rich-text/rich-text.css";

// Light formatting for comments and messages (boss's task «تغییر نمایش فیلد های نوشتن متنی»; docs/tasks/25-rich-text-
// editor.md). They keep their plain-text box (it handles @mentions), so formatting is a few simple marks that the
// toolbar writes and the message view draws:  **پررنگ**   _کج_   ~~خط‌خورده~~   «- » at a line start = bullet,
// «1. » = numbered line. Shift+Enter makes a new line.

const WRAPS = {bold: "**", italic: "_", strike: "~~"};

// apply a format to the selected text of a text box; returns the new value and selection
export function applyFormat(value, selectionStart, selectionEnd, kind) {
    const text = String(value || "");
    const start = Number.isInteger(selectionStart) ? selectionStart : text.length;
    const end = Number.isInteger(selectionEnd) ? selectionEnd : start;
    if (WRAPS[kind]) {
        const mark = WRAPS[kind];
        const selected = text.slice(start, end);
        // already wrapped: remove the marks
        if (text.slice(start - mark.length, start) === mark && text.slice(end, end + mark.length) === mark) {
            return {value: `${text.slice(0, start - mark.length)}${selected}${text.slice(end + mark.length)}`, selectionStart: start - mark.length, selectionEnd: end - mark.length};
        }
        return {value: `${text.slice(0, start)}${mark}${selected}${mark}${text.slice(end)}`, selectionStart: start + mark.length, selectionEnd: end + mark.length};
    }
    // lists: prefix every selected line (or the current one)
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    const nextBreak = text.indexOf("\n", end);
    const lineEnd = nextBreak === -1 ? text.length : nextBreak;
    const lines = text.slice(lineStart, lineEnd).split("\n");
    const listed = lines.map((line, index) => {
        const bare = line.replace(/^(\s*)(?:[-•]\s|\d+[.)]\s)/, "$1");
        return kind === "numbered" ? `${index + 1}. ${bare}` : `- ${bare}`;
    }).join("\n");
    const next = `${text.slice(0, lineStart)}${listed}${text.slice(lineEnd)}`;
    return {value: next, selectionStart: lineStart + listed.length, selectionEnd: lineStart + listed.length};
}

// keyboard shortcuts for a text box: Ctrl/⌘+B and Ctrl/⌘+I
export function formatShortcut(event) {
    if (!(event.ctrlKey || event.metaKey) || event.altKey) return null;
    const key = String(event.key).toLowerCase();
    if (key === "b") return "bold";
    if (key === "i") return "italic";
    return null;
}

const BUTTONS = [
    {kind: "bold", icon: "format_bold", label: "پررنگ (Ctrl+B)"},
    {kind: "italic", icon: "format_italic", label: "کج (Ctrl+I)"},
    {kind: "strike", icon: "strikethrough_s", label: "خط‌خورده"},
    {kind: "bullet", icon: "format_list_bulleted", label: "فهرست گلوله‌ای"},
    {kind: "numbered", icon: "format_list_numbered", label: "فهرست شماره‌دار"},
];

export function FormatToolbar({onFormat, disabled = false, className = ""}) {
    return (
        <div className={`flex align-items-center gap-1 ${className}`} role="toolbar" aria-label="قالب متن">
            {BUTTONS.map((button) => (
                <button
                    key={button.kind}
                    type="button"
                    className="zr-format-btn"
                    title={button.label}
                    aria-label={button.label}
                    disabled={disabled}
                    // keep the focus (and the selection) in the text box
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onFormat(button.kind)}
                >
                    <Icon icon={`mi mi:${button.icon}`} className="text-base"/>
                </button>
            ))}
        </div>
    );
}

// apply a format to a text box element and report the new value; the selection is restored after React renders
export function formatTextBox(element, value, kind, setValue) {
    const result = applyFormat(value, element?.selectionStart, element?.selectionEnd, kind);
    setValue(result.value);
    window.requestAnimationFrame(() => {
        element?.focus();
        element?.setSelectionRange?.(result.selectionStart, result.selectionEnd);
    });
}

const INLINE = /(\*\*[^*\n]+?\*\*|~~[^~\n]+?~~|(?<![\p{L}\p{N}_@])_[^_\n]+?_(?![\p{L}\p{N}_]))/u;

// draw the marks of one piece of text (no mentions inside); emoji keep their images
export function renderFormattedText(text, keyPrefix = "f") {
    const lines = String(text || "").split("\n").map((line) => line
        .replace(/^(\s*)[-•]\s/, "$1• ")
    );
    const out = [];
    lines.forEach((line, lineIndex) => {
        if (lineIndex > 0) out.push("\n");
        line.split(INLINE).forEach((part, index) => {
            if (!part) return;
            const key = `${keyPrefix}-${lineIndex}-${index}`;
            if (/^\*\*[^*\n]+\*\*$/.test(part)) out.push(<strong key={key}><TwemojiText>{part.slice(2, -2)}</TwemojiText></strong>);
            else if (/^~~[^~\n]+~~$/.test(part)) out.push(<s key={key}><TwemojiText>{part.slice(2, -2)}</TwemojiText></s>);
            else if (/^_[^_\n]+_$/.test(part) && INLINE.test(part)) out.push(<em key={key}><TwemojiText>{part.slice(1, -1)}</TwemojiText></em>);
            else out.push(<TwemojiText key={key}>{part}</TwemojiText>);
        });
    });
    return out;
}

// the marks as plain text (previews, notifications): «**a**» → «a»
export const stripFormatting = (text) => String(text || "")
    .replace(/\*\*([^*\n]+?)\*\*/g, "$1")
    .replace(/~~([^~\n]+?)~~/g, "$1")
    .replace(/(?<![\p{L}\p{N}_@])_([^_\n]+?)_(?![\p{L}\p{N}_])/gu, "$1");
